/**
 * usePermission Hook
 * Hook voor permission-based UI rendering.
 * Verbergt elementen op basis van gebruiker rol en permissies.
 */

import React from 'react'
import { useAuthStore } from '../stores/authStore'
import { useQuery } from '@tanstack/react-query'
import api from '../services/api'

/**
 * Hook om te checken of gebruiker een bepaalde permissie heeft.
 * 
 * @param {string} resource - Resource naam (bijv. 'klanten', 'projecten', 'facturen')
 * @param {string} action - Actie (bijv. 'read', 'write', 'delete')
 * @returns {boolean} - True als gebruiker permissie heeft
 */
export function usePermission(resource, action) {
  const user = useAuthStore((state) => state.user)
  
  // Als geen user, geen permissie
  if (!user) {
    return false
  }
  
  // Haal user details op met rol informatie. Non-blocking: placeholder + aggressive caching.
  const { data: userDetails, isLoading, error } = useQuery({
    queryKey: ['user', 'me', user?.id],
    queryFn: async () => {
      const res = await api.get('/auth/me', { timeout: 12000 }).catch((err) => {
        if (import.meta.env.DEV && err?.code !== 'ECONNABORTED') {
          console.warn('[usePermission] auth/me:', err?.message || err)
        } else if (import.meta.env.DEV && err?.code === 'ECONNABORTED') {
          console.warn('[usePermission] auth/me: timeout – controleer of de backend op port 8001 draait')
        }
        return null
      })
      return res?.data ?? null
    },
    enabled: !!user,
    staleTime: 15 * 60 * 1000,      // 15 min: don't refetch on nav/tab switch
    gcTime: 60 * 60 * 1000,         // 1 hour: keep cache even when no observers
    retry: false,                   // No retry on timeout/error - fail fast, use fallback
    refetchOnWindowFocus: false,     // Don't refetch when switching back to tab
    refetchOnMount: false,           // Use cache when navigating - don't block on every mount
    refetchOnReconnect: false,       // Don't refetch on network reconnect
    placeholderData: user ? {
      rol: user.role ? { naam: user.role, permissies: String(user.role).toUpperCase() === 'ADMIN' ? { admin: ['admin'] } : {} } : null,
      is_admin: user.is_admin
    } : undefined, // Non-blocking: instant UI from auth store while /auth/me loads
  })
  
  // Als API call nog laadt of faalt: admin altijd toegang, anders wacht op data
  if (isLoading || error || !userDetails) {
    const isAdmin = user?.is_admin || String(user?.role || '').toUpperCase() === 'ADMIN'
    if (isAdmin) return true
    // Tijdens laden: toon alles zodat de UI niet flikkert, na laden wordt het gefilterd
    if (isLoading) return true
    return false
  }
  
  // SuperUser (rolvlag is_superuser; een ontbrekende tenant maakt niemand superuser, #647)
  if (user?.is_superuser) {
    return true
  }

  // SuperAdmin resource: alleen voor is_superuser (niet voor tenant-admin)
  if (resource === 'superadmin') {
    return false
  }
  
  // Admin (is_admin of role=ADMIN) heeft alle permissies (behalve superadmin)
  if (user?.is_admin || String(user?.role || '').toUpperCase() === 'ADMIN') {
    return true
  }
  
  // Check via rol permissies
  if (userDetails?.rol) {
    const rol = userDetails.rol
    
    // Admin heeft alle permissies
    if (String(rol.naam || '').toUpperCase() === 'ADMIN' || rol.permissies?.admin?.includes('admin')) {
      return true
    }
    
    // Check specifieke resource permissies
    const resourcePerms = rol.permissies?.[resource] || []
    return resourcePerms.includes(action) || resourcePerms.includes('admin')
  }
  
  // Fallback: is_admin heeft alle permissies (backward compatibility)
  if (userDetails?.is_admin) {
    return true
  }
  
  // Geen rol info en geen admin → geen toegang
  return false
}

/**
 * Higher Order Component voor permission-based rendering.
 * 
 * @param {React.Component} Component - Component om te wrappen
 * @param {string} resource - Resource naam
 * @param {string} action - Actie
 * @returns {React.Component|null} - Component of null als geen permissie
 */
export function withPermission(Component, resource, action) {
  return function PermissionWrapper(props) {
    const hasPermission = usePermission(resource, action)
    
    if (!hasPermission) {
      return null
    }
    
    return <Component {...props} />
  }
}

/**
 * Component voor conditional rendering op basis van permissies.
 * 
 * @param {React.ReactNode} children - Children om te renderen
 * @param {string} resource - Resource naam
 * @param {string} action - Actie
 * @returns {React.ReactNode|null} - Children of null
 */
export function PermissionGate({ children, resource, action }) {
  const hasPermission = usePermission(resource, action)
  
  if (!hasPermission) {
    return null
  }
  
  return children
}
